import React from 'react';
import ReactECharts from 'echarts-for-react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiActivity, FiShield, FiTrendingUp, FiPieChart } = FiIcons;

const RiskAnalysisDashboard = ({ goals }) => {
  // 1. Calculate Asset Allocation based on Goal Timeline
  const calculateAllocation = () => {
    let debt = 0;
    let hybrid = 0;
    let equity = 0;

    goals.forEach(goal => {
      const years = parseInt(goal.yearsFromNow);
      const currentCost = parseInt(goal.currentCost);
      // Rough SIP calc to weight the portfolio
      const inflation = 6.5; 
      const fv = currentCost * Math.pow(1 + inflation/100, years);
      // Simple approximate weight based on Future Value for visualization
      const weight = fv; 

      if (years <= 3) debt += weight;
      else if (years <= 9) hybrid += weight;
      else equity += weight;
    });

    return { debt, hybrid, equity };
  };

  const { debt, hybrid, equity } = calculateAllocation();
  const total = debt + hybrid + equity || 1;

  // Chart 1: Asset Allocation Pie
  const assetOption = {
    tooltip: { trigger: 'item' },
    legend: { bottom: '5%', left: 'center' },
    color: ['#EF4444', '#F59E0B', '#10B981'], // Red (Debt/Short), Orange (Hybrid), Green (Equity)
    series: [
      {
        name: 'Asset Allocation',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 18,
            fontWeight: 'bold'
          }
        },
        data: [
          { value: Math.round(debt), name: 'Debt / Liquid (Short Term)' },
          { value: Math.round(hybrid), name: 'Hybrid (Med Term)' },
          { value: Math.round(equity), name: 'Equity (Long Term)' }
        ]
      }
    ]
  };

  // Chart 2: Risk Profile Gauge (Calculated based on Weighted Average Time Horizon)
  const calculateRiskScore = () => {
    let weightedYears = 0;
    let totalValue = 0;
    goals.forEach(goal => {
        const val = parseInt(goal.currentCost);
        weightedYears += val * parseInt(goal.yearsFromNow);
        totalValue += val;
    });
    const avgYears = totalValue > 0 ? weightedYears / totalValue : 0;
    // Map avg years to score 0-100
    // 0 years = 0 (Conservative), 15 years = 100 (Aggressive)
    return Math.min(Math.round((avgYears / 15) * 100), 100);
  };
  
  const riskScore = calculateRiskScore();

  const riskOption = {
    series: [
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 100,
        splitNumber: 5,
        itemStyle: {
          color: '#6366f1' // Indigo
        },
        progress: {
          show: true,
          width: 18
        },
        pointer: {
          icon: 'path://M12.85 5.71l-1.41-1.42L5.7 10 17 21.31l11.31-11.31-1.41-1.42L17 18.48z',
          length: '12%',
          width: 8,
          offsetCenter: [0, '-60%'],
          itemStyle: {
            color: 'auto'
          }
        },
        axisLine: {
          lineStyle: {
            width: 18,
            color: [
                [0.3, '#10B981'], // Green (Safe/Conservative)
                [0.7, '#F59E0B'], // Orange (Moderate)
                [1, '#EF4444']    // Red (Aggressive)
            ]
          }
        },
        axisTick: { show: false },
        splitLine: { length: 10, lineStyle: { width: 2, color: '#999' } },
        axisLabel: { show: false },
        title: { offsetCenter: [0, '30%'], fontSize: 16 },
        detail: {
          valueAnimation: true,
          fontSize: 24,
          offsetCenter: [0, '0%'],
          formatter: '{value}',
          color: 'inherit'
        },
        data: [{ value: riskScore, name: 'Portfolio Aggression Score' }]
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8 break-inside-avoid"
    >
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiActivity} className="text-2xl text-cyan-400" />
          <div>
            <h2 className="text-xl font-bold">Risk Analysis Dashboard</h2>
            <p className="text-gray-400 text-sm">Visual breakdown of your goal-based portfolio</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 p-6">
        {/* Asset Allocation */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center justify-center">
                <SafeIcon icon={FiPieChart} className="mr-2 text-indigo-600" />
                Asset Allocation
            </h3>
            <div className="h-[250px] w-full">
                <ReactECharts option={assetOption} style={{ height: '100%', width: '100%' }} />
            </div>
            <p className="text-xs text-center text-gray-500 mt-2">
                Based on the future value weight of your goals
            </p>
        </div>

        {/* Risk Score */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="mr-2 text-indigo-600" />
                Risk Profile Gauge
            </h3>
            <div className="h-[250px] w-full">
                <ReactECharts option={riskOption} style={{ height: '100%', width: '100%' }} />
            </div>
            <p className="text-xs text-center text-gray-500 mt-2">
                Calculated from the weighted average duration of your goals. 
                <br/>Higher score = Longer duration = Higher Equity Capacity.
            </p>
        </div>
      </div>

      <div className="bg-indigo-50 p-4 mx-6 mb-6 rounded-lg border border-indigo-100 flex items-start space-x-3">
        <SafeIcon icon={FiShield} className="text-indigo-600 mt-1 flex-shrink-0" />
        <div>
            <h4 className="font-semibold text-indigo-900 text-sm">Analyst Note</h4>
            <p className="text-indigo-800 text-sm">
                Your portfolio is split {Math.round((equity/total)*100)}% in Equity and {Math.round(((debt+hybrid)/total)*100)}% in Debt/Hybrid. 
                {equity/total > 0.7 
                    ? " This is an aggressive portfolio suitable for long-term wealth creation. Ensure you have patience for market volatility." 
                    : " This is a balanced/conservative portfolio focused on stability. It may have lower returns but offers higher capital protection."}
            </p>
        </div>
      </div>
    </motion.div>
  );
};

export default RiskAnalysisDashboard;